import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  MAX_TIMER_HOURS,
  MAX_TIMER_SECONDS,
  clock,
  duration,
  type RunningTimer,
  type TimerEntity,
  type TimerSummary,
} from '@goyone/shared';
import { api } from '../lib/api';
import { Icon } from './Icon';
import { useToast } from './Toast';

/**
 * مؤقّت العمل في الشريط العلوي.
 *
 * ## العدّاد يمضي في المتصفح والرقم يأتي من الخادم
 *
 * الخادم يُرسل `elapsed` لحظة القراءة، والمتصفح يزيد ثانيةً كل ثانية فوقه.
 * ولا يُحسب من `startedAt` وحدها: ساعة الجهاز قد تكون متأخّرة ساعتين، فيرى
 * صاحبها عدّادًا سالبًا وهو لا يعلم أن الخلل في جهازه.
 *
 * ولا طلبٌ كل ثانية: الطلب كل نصف دقيقة لتصحيح الانحراف، والعدّ بينهما
 * محلّي.
 */

/** كم ثانية بين مزامنة وأخرى مع الخادم. */
const SYNC_SECONDS = 30;

export function WorkTimerBadge() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [ticks, setTicks] = useState(0);

  const summary = useQuery({
    queryKey: ['timer', 'summary'],
    queryFn: () => api<TimerSummary>('/timer/summary'),
    refetchInterval: SYNC_SECONDS * 1000,
  });

  const running = summary.data?.running.entry ?? null;

  /* العدّاد يمضي فقط حين يكون هناك ما يُعدّ: مؤقّتٌ موقوف لا يُوقظ الصفحة. */
  useEffect(() => {
    if (!running) return;
    const id = window.setInterval(() => setTicks((t) => t + 1), 1000);
    return () => window.clearInterval(id);
  }, [running]);

  const stop = useMutation({
    mutationFn: () => api<{ discarded: boolean }>('/timer/stop', { method: 'POST', body: {} }),
    onSuccess: (res) => {
      void queryClient.invalidateQueries({ queryKey: ['timer'] });
      toast(
        res.discarded
          ? 'المدة أقصر من دقيقة — لم تُحفظ.'
          : 'أُوقف المؤقّت وسُجّل الوقت.',
      );
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الإيقاف.'),
  });

  if (!running) {
    /* بلا مؤقّت: مجموع اليوم وحده — رقمٌ يُقرأ بطرف العين ولا يشغل مكانًا. */
    const today = summary.data?.todaySeconds ?? 0;
    if (!today) return null;

    return (
      <span className="timer-badge timer-idle" title={`مجموع اليوم: ${duration(today)}`}>
        <Icon name="clock" size={14} />
        <span className="num">{clock(today)}</span>
      </span>
    );
  }

  /*
   * ما مضى: ما أرسله الخادم زائد ما عدّه المتصفح بعده.
   *
   * والقصّ هنا كما في الخادم: عدّادٌ يتجاوز السقف ثم يُحفظ أصغر منه يجعل
   * المستخدم يظنّ أن وقته ضاع.
   */
  const base = summary.data?.running.elapsed ?? 0;
  const shown = Math.min(base + ticks, MAX_TIMER_SECONDS);
  const over = base + ticks > MAX_TIMER_SECONDS;

  return (
    <span className={`timer-badge${over ? ' timer-over' : ''}`}>
      <span className="timer-pulse" aria-hidden="true" />
      <span className="num timer-clock">{clock(shown)}</span>

      <span className="timer-on" title={running.entityName ?? undefined}>
        {running.entityName ?? 'سجل'}
      </span>

      <button
        className="btn btn-sm btn-danger"
        onClick={() => stop.mutate()}
        disabled={stop.isPending}
      >
        أوقف
      </button>

      {/*
        تجاوز السقف يُعلَن لا يُخفى.
        مؤقّتٌ نُسي ليلةً كاملة سيُحفظ مقصوصًا، ومن حقّ صاحبه أن يعرف قبل
        أن يقرأ رقمًا لا يطابق ما رآه.
      */}
      {over && (
        <span className="tiny" title={`لا يُسجَّل أكثر من ${MAX_TIMER_HOURS} ساعة`}>
          تجاوز الحدّ
        </span>
      )}
    </span>
  );
}

/* ==========================================================================
   زرّ البدء على سجل
   ========================================================================== */

/**
 * زرّ يبدأ المؤقّت على هذا السجل، أو يوقفه إن كان يعمل عليه.
 *
 * وإن كان يعمل على سجلٍ آخر فالزرّ يقول ذلك: البدء سيوقف ذاك، ومن حقّ
 * المستخدم أن يعرف ما سيُوقفه قبل أن يضغط.
 */
export function TimerButton({
  entity,
  entityId,
  className = 'btn btn-sm',
}: {
  entity: TimerEntity;
  entityId: string;
  className?: string;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const running = useQuery({
    queryKey: ['timer', 'running'],
    queryFn: () => api<RunningTimer>('/timer/running'),
  });

  const current = running.data?.entry ?? null;
  const onThis = current?.entity === entity && current?.entityId === entityId;
  const onOther = current !== null && !onThis;

  const start = useMutation({
    mutationFn: () => api('/timer/start', { method: 'POST', body: { entity, entityId } }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['timer'] });
      toast(onOther ? `أُوقف «${current?.entityName ?? 'السابق'}» وبدأ هذا` : 'بدأ المؤقّت');
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر بدء المؤقّت.'),
  });

  const stop = useMutation({
    mutationFn: () => api<{ discarded: boolean }>('/timer/stop', { method: 'POST', body: {} }),
    onSuccess: (res) => {
      void queryClient.invalidateQueries({ queryKey: ['timer'] });
      toast(res.discarded ? 'المدة أقصر من دقيقة — لم تُحفظ.' : 'سُجّل الوقت');
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الإيقاف.'),
  });

  if (onThis) {
    return (
      <button
        className="btn btn-sm btn-danger"
        onClick={() => stop.mutate()}
        disabled={stop.isPending}
      >
        <Icon name="clock" size={14} />
        أوقف المؤقّت
      </button>
    );
  }

  return (
    <button
      className={className}
      onClick={() => start.mutate()}
      disabled={start.isPending}
      title={onOther ? `سيُوقف المؤقّت العامل على «${current?.entityName ?? 'سجل آخر'}»` : undefined}
    >
      <Icon name="clock" size={14} />
      {onOther ? 'ابدأ هنا بدلًا' : 'ابدأ المؤقّت'}
    </button>
  );
}

/* ==========================================================================
   قيود سجل
   ========================================================================== */

/**
 * الوقت المبذول على سجل.
 *
 * يراه كل من يرى السجل لا صاحب القيد وحده: الوقت المبذول معلومةٌ عن العميل
 * لا عن الموظف، ومن يتسلّم ملفًّا يحتاج أن يعرف كم بُذل فيه قبله.
 */
export function TimeLog({
  entity,
  entityId,
  title = 'الوقت المبذول',
}: {
  entity: TimerEntity;
  entityId: string;
  title?: string;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [adding, setAdding] = useState(false);
  const [minutes, setMinutes] = useState('30');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ['timer', 'entries', entity, entityId],
    queryFn: () => api<TimeEntryList>(`/timer/entries?entity=${entity}&entityId=${entityId}`),
    enabled: Boolean(entityId),
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['timer'] });

  const add = useMutation({
    mutationFn: () =>
      api('/timer/entries', {
        method: 'POST',
        body: {
          entity,
          entityId,
          minutes: Number(minutes),
          /* البداية محسوبة بالطرح من الآن: من يكتب يدويًّا يتذكّر المدة لا الساعة. */
          startedAt: new Date(Date.now() - Number(minutes) * 60_000).toISOString(),
          note: note.trim() || undefined,
        },
      }),
    onSuccess: () => {
      setAdding(false);
      setNote('');
      setError(null);
      void refresh();
      toast('سُجّل الوقت');
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'تعذّر التسجيل.'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api(`/timer/entries/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void refresh();
      toast('حُذف القيد');
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'تعذّر الحذف.'),
  });

  const entries = list.data ?? [];
  const total = entries.reduce((s, e) => s + (e.seconds ?? 0), 0);

  return (
    <div className="card">
      <div className="card-head">
        <h2 className="card-title">{title}</h2>
        {total > 0 && <span className="tiny mute">{duration(total)}</span>}
      </div>

      <div className="card-body">
        {error && <div className="alert alert-danger mb-4">{error}</div>}

        <div className="row" style={{ gap: 'var(--sp-2)', flexWrap: 'wrap' }}>
          <TimerButton entity={entity} entityId={entityId} />
          <button className="btn btn-sm btn-ghost" onClick={() => setAdding((v) => !v)}>
            <Icon name="plus" size={14} />
            سجّل وقتًا مضى
          </button>
        </div>

        {/*
          القيد اليدوي لمن نسي أن يبدأ المؤقّت — وهو الغالب.
          ومنعُه يجعل المؤقّت يقيس من يتذكّره وحده، فتصير أرقامه عن الذاكرة
          لا عن العمل.
        */}
        {adding && (
          <div className="timer-manual mt-3">
            <label className="field">
              <span className="label">المدة بالدقائق</span>
              <input
                className="input"
                type="number"
                min={1}
                max={MAX_TIMER_HOURS * 60}
                value={minutes}
                onChange={(e) => setMinutes(e.target.value)}
              />
            </label>
            <label className="field" style={{ flex: 1 }}>
              <span className="label">على ماذا؟ (اختياري)</span>
              <input
                className="input"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="مراجعة العرض، مكالمة، تجهيز مستندات…"
              />
            </label>
            <button
              className="btn btn-primary btn-sm"
              onClick={() => add.mutate()}
              disabled={add.isPending || !Number(minutes)}
            >
              حفظ
            </button>
          </div>
        )}

        {list.isPending && <div className="skeleton mt-4" style={{ height: 80 }} />}

        {list.data && entries.length === 0 && (
          <p className="mute small center mt-4">لم يُسجَّل وقتٌ على هذا السجل بعد.</p>
        )}

        {entries.length > 0 && (
          <ul className="timer-list mt-4">
            {entries.map((e) => (
              <li key={e.id} className="timer-row">
                <span className="num timer-row-time">{clock(e.seconds ?? 0)}</span>
                <span className="timer-row-main">
                  <strong className="small">{e.userName ?? 'مستخدم سابق'}</strong>
                  {e.note && <span className="tiny mute"> — {e.note}</span>}
                  {e.manual && <span className="tiny mute"> · يدوي</span>}
                  {e.clipped && (
                    <span className="tiny" title={`قُصّ عند ${MAX_TIMER_HOURS} ساعة`}>
                      {' '}
                      · مقصوص
                    </span>
                  )}
                </span>
                <button
                  className="link-btn tiny link-danger"
                  onClick={() => remove.mutate(e.id)}
                  disabled={remove.isPending}
                >
                  حذف
                </button>
              </li>
            ))}
          </ul>
        )}

        <p className="tiny mute mt-3">
          أقلّ مدة تُحفظ دقيقة، وأقصى قيدٍ واحد {MAX_TIMER_HOURS} ساعة.{' '}
          <Link to="/agency/my" className="tiny">
            مهامي
          </Link>
        </p>
      </div>
    </div>
  );
}

type TimeEntryList = Array<{
  id: string;
  userName: string | null;
  seconds: number | null;
  note: string | null;
  manual: boolean;
  clipped: boolean;
}>;
